import { NetworkStatus, useQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Icon,
  PrimaryFullScreenSpinner,
  StatusBase,
  StatusUI,
  Txt,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { ClockStopwatch } from "@untitledui/icons";
import { DateTime } from "luxon";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AdminTds2ItemFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const MATURING_SOON_WITHIN_DAYS = 14;

export const Route = createFileRoute("/__auth/tds2")({
  head: () => opsHead("Term Deposit Queue"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const tds2PageDocument = graphql(`
  query AdminTds2Page($after: String, $first: Int!) {
    termDepositQueue(after: $after, first: $first) {
      items {
        ...AdminTds2Item
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }

  fragment AdminTds2Item on TermDepositAsset {
    id
    businessEntity {
      organisation {
        id
        name
        squareLogoUrl
      }
    }
    ...AssetCardFields
  }
`);

function View() {
  const baseInput = { first: 50 };

  const { data, loading, networkStatus, fetchMore } = useQuery(
    tds2PageDocument,
    {
      variables: { after: null, ...baseInput },
      notifyOnNetworkStatusChange: true,
    },
  );

  const rows = data?.termDepositQueue.items ?? [];

  const nextCursor = data?.termDepositQueue.pageInfo.hasNextPage
    ? (data.termDepositQueue.pageInfo.endCursor ?? undefined)
    : undefined;

  const isLoading = loading || networkStatus === NetworkStatus.fetchMore;

  return (
    <AdminPageContainer testId="admin-tds2">
      <AdminPageHeader title="Term Deposit Queue" />
      <DataTable
        data={rows}
        columns={columns}
        isLoadingData={isLoading}
        manualPagination
        manualSorting
        pageSize={10_000}
        className="overflow-hidden border"
        hidePagination
      />
      <div className="mt-4 flex w-full flex-row justify-center">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({ variables: { ...baseInput, after: nextCursor } })
            }
            variant="outline"
            loading={isLoading}
          >
            Load more
          </Button>
        ) : (
          !!rows.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
      </div>
    </AdminPageContainer>
  );
}

type Row = AdminTds2ItemFragment;

function getDaysUntilEnd(end: Date): number {
  return DateTime.fromJSDate(end).diffNow("days").days;
}

function isMaturingSoon(daysUntilEnd: number): boolean {
  return daysUntilEnd >= 0 && daysUntilEnd <= MATURING_SOON_WITHIN_DAYS;
}

function formatDaysUntil(daysUntilEnd: number): string {
  const roundedDays = Math.round(daysUntilEnd);
  if (roundedDays === 0) {
    return "Today";
  }
  if (roundedDays === 1) {
    return "Tomorrow";
  }
  return `in ${roundedDays} days`;
}

const columns: ColumnDef<Row>[] = [
  {
    id: "status",
    enableSorting: false,
    accessorFn: (row) => row.assetStatus,
    header: "Status",
    cell: ({ row }) => {
      const daysUntilEnd = getDaysUntilEnd(row.original.end);
      if (isMaturingSoon(daysUntilEnd)) {
        return (
          <StatusBase
            color="warning"
            label="Maturing soon"
            icon={ClockStopwatch}
          />
        );
      }
      return <StatusUI status={row.original.assetStatus} />;
    },
  },
  {
    id: "organisation",
    enableSorting: false,
    accessorFn: (row) => row.businessEntity?.organisation?.name ?? "",
    header: "Organisation",
    cell: ({ row }) => {
      const organisation = row.original.businessEntity?.organisation;
      return organisation ? (
        <Link
          to="/organisations/$organisationId"
          params={{ organisationId: organisation.id }}
          className="flex items-center gap-2"
        >
          <Icon imageUrl={organisation.squareLogoUrl ?? undefined} size="8" />
          <Txt variant="name">{organisation.name}</Txt>
        </Link>
      ) : null;
    },
  },
  {
    id: "account",
    enableSorting: false,
    accessorFn: (row) => row.name,
    header: "Account",
    cell: ({ row }) => (
      <AccountCard
        account={row.original}
        subText={["BUSINESS_ENTITY", "PROVIDER"]}
        hideTdProgress
        hideTdBadge
        linkTo={`/assets/${row.original.id}`}
      />
    ),
  },
  {
    id: "maturity",
    enableSorting: false,
    accessorFn: (row) => row.end.getTime(),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Maturity Date"
        align="right"
      />
    ),
    cell: ({ row }) => {
      const end = row.original.end;
      const daysUntilEnd = getDaysUntilEnd(end);
      return (
        <div className="flex flex-col items-end">
          <DateUI date={end} />
          {isMaturingSoon(daysUntilEnd) && (
            <Txt variant="caption" tone="tertiary">
              ({formatDaysUntil(daysUntilEnd)})
            </Txt>
          )}
        </div>
      );
    },
  },
  {
    id: "balance",
    enableSorting: false,
    accessorFn: (row) => row.estimatedValue?.amount?.toNumber() ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Balance" align="right" />
    ),
    cell: ({ row }) =>
      row.original.estimatedValue ? (
        <div className="flex justify-end">
          <CurrencyAmountUI {...row.original.estimatedValue} />
        </div>
      ) : (
        "-"
      ),
  },
];
